import { useRef, useState, useEffect, useCallback } from 'react';
import type { MouseEvent, RefObject, ReactElement } from 'react';
import { useTracking } from 'react-tracking';
import Media from 'react-media';
import { debounce } from 'lodash';
import loadable from '@loadable/component';

import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';
import { Textfield, useTheme } from '@simplywallst/ui-core';
import type { TextfieldStyleType } from '@simplywallst/ui-core';

import useWindowSize from '@/hooks/useWindowSize';

import { Modal } from '@simplywallst/ui-core';
import SearchFieldPostfix from '@/components/SearchFieldPostfix';

import type { Option } from '../Options';
import Options, { Item, Prefix } from '../Options';
import type { DisplayType } from './styled';
import {
  DropdownWrapper,
  Select,
  OverlayTrigger,
  ChevronDownIcon,
  Wrapper,
} from './styled';

import { filterOptions } from './utils';

const SearchOverlay = loadable(
  () => import('@/components/Search/components/SearchOverlay')
);

const empty = 'empty';
const emptyMessage = 'No results';
const renderDefaultEmptyState = () => (
  <Item selected={false} disabled={true}>
    {emptyMessage}
  </Item>
);

export interface ButtonRenderProps {
  value: string;
  name?: string;
  active?: boolean;
  innerRef: RefObject<HTMLButtonElement>;
  onBlur: () => void;
  onFocus: () => void;
  onClick: (e: MouseEvent) => void;
}

export interface ExtraToggleProps {
  onBlur?: () => void;
  onFocus?: () => void;
}
export interface EmptyStateProps {
  onBlur?: () => void;
  onFocus?: () => void;
}

export interface Props {
  name?: string;
  label?: string;
  error?: string;
  options: Option[];
  align?: 'left' | 'right';
  autoComplete?: 'new-password' | 'off' | 'no';
  value: string;
  displayType?: DisplayType;
  placeholder?: string;
  canSearch?: boolean;
  styleType?: TextfieldStyleType;
  searchStatus?: 'success' | 'pending' | 'error';
  trackingProps?: Record<string, string | number>;
  emptyMessage?: string;
  tabIndex?: number;
  onSelect: (option: string) => void;
  buttonRender?: (
    props: ButtonRenderProps
  ) => ReactElement<ButtonRenderProps>[] | ReactElement<ButtonRenderProps>;
  emptyState?: (props: EmptyStateProps) => ReactElement<EmptyStateProps>;
  extraToggle?: (props: ExtraToggleProps) => ReactElement<ExtraToggleProps>;
  isMinimal?: boolean;
  width?: number;
  underline?: boolean;
  showModalSm?: boolean;
  disabled?: boolean;
}

const labelReducer = (options: Option[]): Record<string, string> =>
  options.reduce(
    (a, b) => {
      a[b.option] = b.label;
      return a;
    },
    {
      [empty]: '',
    }
  );

const Dropdown = ({
  name,
  align = 'left',
  buttonRender,
  options,
  value,
  label,
  styleType,
  canSearch = false,
  searchStatus,
  placeholder,
  tabIndex = 1,
  onSelect,
  displayType,
  autoComplete = 'off',
  isMinimal,
  width,
  extraToggle,
  underline = true,
  showModalSm = true,
  trackingProps,
  emptyState = renderDefaultEmptyState,
  disabled = false,
}: Props) => {
  const theme = useTheme();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: name || undefined,
    ...trackingProps,
  });
  const [open, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchQuery, setSearchQuery] = useState('');
  const queryRef = useRef<number>(0);
  const textfieldRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const labelMap = useRef<Record<string, string>>(labelReducer(options));
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const { width: screenWidth = 0 } = useWindowSize();

  const debounceTrackingEvent = useRef(
    debounce(
      (query) =>
        trackEvent({
          type: 'track',
          action: 'typing',
          keyword: query,
        }),
      500
    )
  );

  useEffect(() => {
    setFilteredOptions(filterOptions(options, searchQuery));
    if (searchQuery) {
      debounceTrackingEvent.current(searchQuery);
    }
  }, [options, searchQuery]);

  useEffect(() => {
    if (RUNTIME_ENV === 'native') {
      async function setAccessoryBar() {
        const { Device } = await import('@capacitor/device');
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'ios') {
          const { Keyboard } = await import('@capacitor/keyboard');
          Keyboard.setAccessoryBarVisible({ isVisible: true });
        }
      }

      async function unsetAccessoryBar() {
        const { Device } = await import('@capacitor/device');
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'ios') {
          const { Keyboard } = await import('@capacitor/keyboard');
          Keyboard.setAccessoryBarVisible({ isVisible: false });
        }
      }
      setAccessoryBar();
      return () => {
        unsetAccessoryBar();
      };
    }
  }, [open]);

  useEffect(() => {
    if (open && value.length > 0 && filteredOptions.length) {
      setHoveredIndex(filteredOptions.map((n) => n.option).indexOf(value));
    }
  }, [filteredOptions, open, value]);

  const onReset = useCallback(() => {
    setSearchQuery('');
    setHoveredIndex(-1);
  }, []);

  const onBlur = useCallback(() => {
    if (screenWidth > theme.mdMinRaw) {
      // Using a queuing system to help tune for accessibility https://stackoverflow.com/a/38317768/2248347
      queryRef.current = window.setTimeout(
        () => {
          setOpen(false);
          onReset();
        },
        extraToggle ? 200 : 0
      );
    } else {
      setOpen(false);
      onReset();
    }
  }, [screenWidth]);

  const onButtonClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { current } = buttonRef;
    if (current !== null && !open && document.activeElement !== current) {
      current.focus();
    }
    if (textfieldRef.current) {
      textfieldRef.current.focus();
    }
  };

  const handleSelect = useCallback(
    (option: string, index: number) => {
      if (option.length === 0) return;
      onBlur();
      onSelect && onSelect(option);
      trackEvent({
        action: 'select',
        option,
        index,
      });
    },
    [onBlur, onSelect, trackEvent]
  );

  const onPostfixClick = () => {
    const { current } = textfieldRef;
    if (current) {
      current.focus();
    }
  };

  const onKeydown = useCallback(
    (e) => {
      let nextHoveredIndex = hoveredIndex;
      if (e.keyCode === 38 && open) {
        e.preventDefault();
        if (!filteredOptions.length) return;
        if (hoveredIndex > 0) nextHoveredIndex = hoveredIndex - 1;
        if (hoveredIndex === -1) nextHoveredIndex = filteredOptions.length - 1;
        if (hoveredIndex === 0) nextHoveredIndex = filteredOptions.length - 1;
        setHoveredIndex(nextHoveredIndex);
      }
      if (e.keyCode === 40 && open) {
        e.preventDefault();
        if (!filteredOptions.length) return;
        if (hoveredIndex < filteredOptions.length - 1)
          nextHoveredIndex = hoveredIndex + 1;
        if (hoveredIndex === -1) nextHoveredIndex = 0;
        if (hoveredIndex === filteredOptions.length - 1) nextHoveredIndex = 0;
        setHoveredIndex(nextHoveredIndex);
      }
      if (e.keyCode === 13) {
        e.preventDefault();
        if (open) {
          if (hoveredIndex > -1 && filteredOptions.length)
            handleSelect(filteredOptions[hoveredIndex].option, hoveredIndex);
        } else {
          setOpen(true);
        }
      }
      if (e.keyCode === 27) {
        e.preventDefault();
        onBlur();
      }
    },
    [hoveredIndex, open, filteredOptions, handleSelect, onBlur]
  );

  useEffect(() => {
    const { current } = textfieldRef;
    if (current) {
      current.addEventListener('keydown', onKeydown);
    }
    return () => {
      const { current } = textfieldRef;
      if (current) {
        current.removeEventListener('keydown', onKeydown);
      }
    };
  }, [onKeydown]);

  const onFocus = () => {
    window.clearTimeout(queryRef.current);
    !open && setOpen(true);
  };

  const onClick = () => {
    !open && setOpen(true);
    if (screenWidth > theme.mdMinRaw) {
      const { current } = textfieldRef;
      if (current) {
        current.focus();
      }
    }
  };

  const getValue = useCallback(() => {
    if (canSearch && open) return searchQuery;
    if (typeof value === 'undefined' || value.length === 0) {
      return labelMap.current[empty];
    }
    return labelMap.current[value];
  }, [canSearch, open, searchQuery, value]);

  return (
    <Track>
      <DropdownWrapper
        data-cy-id={`dropdown-${name}`}
        displayType={displayType}
      >
        {buttonRender ? (
          buttonRender({
            onBlur,
            onFocus,
            onClick: onButtonClick,
            name,
            value: labelMap.current[value.length > 0 ? value : empty],
            innerRef: buttonRef,
            active: open,
          })
        ) : (
          <Textfield
            name={name}
            ref={!isMinimal ? textfieldRef : null}
            type={canSearch ? 'search' : 'text'}
            styleType={styleType}
            label={label}
            postfix={
              canSearch && open ? (
                <SearchFieldPostfix
                  onReset={onReset}
                  searchQuery={searchQuery}
                  searchStatus={searchStatus}
                />
              ) : (
                <ChevronDownIcon fillOpacity="0.5" onClick={onPostfixClick} />
              )
            }
            value={getValue()}
            placeholder={placeholder}
            autoComplete={autoComplete}
            onChange={(e) => {
              if (canSearch) {
                setSearchQuery(e.currentTarget.value);
                setHoveredIndex(-1);
              }
            }}
            readOnly={
              canSearch === false || screenWidth < theme.mdMinRaw || !open
            }
            onFocus={() => {
              onFocus();
              trackEvent({ action: 'focus' });
            }}
            onBlur={onBlur}
            tabIndex={
              canSearch === false && screenWidth < theme.mdMinRaw
                ? -1
                : tabIndex
            }
            disabled={disabled}
          />
        )}
        <Media query={{ maxWidth: theme.mdMinRaw }}>
          {canSearch === false ? (
            <Select
              data-cy-id={`dropdown-${name}-select`}
              name={name}
              tabIndex={tabIndex}
              value={value}
              onBlur={onBlur}
              onChange={(e) =>
                handleSelect(
                  e.currentTarget.value,
                  e.currentTarget.selectedIndex
                )
              }
            >
              {placeholder && !canSearch && (
                <option value="">{placeholder}</option>
              )}
              {filteredOptions.length ? (
                filteredOptions.map((n) => (
                  <option
                    key={`dropdown-select-option-${n.option}-${n.label}`}
                    data-cy-id="dropdown-option"
                    value={n.option}
                  >
                    {n.label}
                  </option>
                ))
              ) : (
                <option value="" data-cy-id="dropdown-option">
                  {emptyMessage}
                </option>
              )}
            </Select>
          ) : (
            <OverlayTrigger
              onClick={onClick}
              data-cy-id={`dropdown-overlay-trigger-${name}`}
            />
          )}
        </Media>
        <Media query={{ maxWidth: theme.smMaxRaw }}>
          {showModalSm && open && (
            <Modal isNaked onSecondaryClick={onBlur}>
              <SearchOverlay
                styleType="light"
                onClose={onBlur}
                onChange={setSearchQuery}
                placeholder={placeholder}
              >
                {extraToggle && extraToggle({})}
                {filteredOptions.length
                  ? filteredOptions.map((n, i) => (
                      <Item
                        key={`dropdown-search-overlay-item-${n.option}-${n.label}`}
                        underline={underline}
                        onClick={() => handleSelect(n.option, i)}
                        selected={value === n.option}
                        disabled={n.option.length === 0}
                        level={n.level}
                      >
                        {n.option.length > 0 && value === n.option && (
                          <Prefix>
                            <CheckSolid />
                          </Prefix>
                        )}
                        {n.label}
                      </Item>
                    ))
                  : emptyState({})}
              </SearchOverlay>
            </Modal>
          )}
        </Media>
        <Media query={{ minWidth: theme.mdMinRaw }}>
          {isMinimal && open ? (
            <Wrapper width={width} align={align}>
              {canSearch && (
                <Textfield
                  styleType="standardReverse"
                  name={name}
                  ref={isMinimal ? textfieldRef : null}
                  type={'search'}
                  postfix={
                    <SearchFieldPostfix
                      onReset={onReset}
                      searchQuery={searchQuery}
                      searchStatus={searchStatus}
                    />
                  }
                  value={searchQuery}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  onChange={(e) => {
                    if (canSearch) {
                      setSearchQuery(e.currentTarget.value);
                      setHoveredIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    onFocus();
                    trackEvent({ action: 'focus' });
                  }}
                  onBlur={onBlur}
                />
              )}
              <Options
                searchable
                visible={open}
                selected={value}
                align={align}
                hoveredIndex={hoveredIndex}
                options={filteredOptions}
                scrollToSelected={searchQuery.length === 0 && !extraToggle}
                onBlur={onBlur}
                onFocus={onFocus}
                onSelect={handleSelect}
                width={width}
                extraItem={extraToggle}
                placeholder={placeholder}
                canSearch={canSearch}
                emptyState={emptyState}
              />
            </Wrapper>
          ) : (
            <Options
              visible={open}
              selected={value}
              align={align}
              hoveredIndex={hoveredIndex}
              options={filteredOptions}
              scrollToSelected={searchQuery.length === 0}
              onBlur={onBlur}
              onFocus={onFocus}
              onSelect={handleSelect}
              width={width}
              placeholder={placeholder}
              canSearch={canSearch}
              emptyState={emptyState}
            />
          )}
        </Media>
      </DropdownWrapper>
    </Track>
  );
};

export default Dropdown;
